import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AlertsType } from '@rappi/ui/alerts';

import { AlertsService } from '../../../../../services/alerts/alerts.service';
import { MakersFinancesMgrService } from '../../../../services/makers-finances-mgr.service';
import { SelectOption } from '../../../purchase-order-detail/sections/section-base.types';
import { FH_COMPANY_LABEL } from '../../company-form.constants';

export const CONTACT_DIALOG_LABEL = {
  TITLE:       'Create contact',
  NAME:        'Name',
  EMAIL:       'Email',
  PHONE:       'Phone',
  ROLE:        'Role',
  CONFIRM:     'Create contact',
  CREATED:     'Contact created.',
  FAILED:      'Could not create the contact. Please try again.',
  REQUIRED:    '*Required',
  EMAIL_ERROR: 'Enter a valid email address.',
} as const;

export interface ContactCreateDialogData {
  /** Present in edit mode only — there is no company id while creating a company. */
  companyId?: string;
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Inline creation behind the "Create new contact" link under the Legal POC / Financial POC
// selects. Resolves with the created option so the page can select it immediately.
//
// Confirmed against docs/swagger.json (dto/contact.CreateRequest): POST /contacts has no
// `company_id` at all — contacts are not created scoped to a company, they're attached
// afterwards via the company's legal_poc_id / financial_poc_id (which is exactly how this dialog
// is used). So `data.companyId` is accepted for signature compatibility but intentionally unused.
@Component({
  selector: 'app-contact-create-dialog',
  templateUrl: './contact-create-dialog.component.html',
  styleUrls: ['./contact-create-dialog.component.scss'],
})
export class ContactCreateDialogComponent {
  readonly LABELS = CONTACT_DIALOG_LABEL;
  readonly COMMON = FH_COMPANY_LABEL;

  name = '';
  email = '';
  phone = '';
  role = '';
  submitted = false;
  isSaving = false;

  constructor(
    public readonly dialogRef: MatDialogRef<ContactCreateDialogComponent, SelectOption | null>,
    private readonly financesService: MakersFinancesMgrService,
    private readonly alerts: AlertsService,
    @Inject(MAT_DIALOG_DATA) public readonly data: ContactCreateDialogData,
  ) {}

  get hasNameError(): boolean {
    return this.submitted && !this.name.trim();
  }

  get hasEmailError(): boolean {
    return this.submitted && !EMAIL_PATTERN.test(this.email.trim());
  }

  cancel(): void {
    this.dialogRef.close(null);
  }

  confirm(): void {
    this.submitted = true;
    if (this.hasNameError || this.hasEmailError || this.isSaving) {
      return;
    }

    this.isSaving = true;
    this.financesService.createContact({
      full_name: this.name.trim(),
      email: this.email.trim(),
      phone: this.phone.trim() || undefined,
      job_role: this.role.trim() || undefined,
    }).subscribe({
      next: res => {
        this.isSaving = false;
        this.alerts.openAlerts(this.LABELS.CREATED, AlertsType.success);
        this.dialogRef.close({
          id: String(res.data.id),
          name: `${res.data.full_name}  ·  ${res.data.email}`,
        });
      },
      error: () => {
        this.isSaving = false;
        this.alerts.openAlerts(this.LABELS.FAILED, AlertsType.error);
      },
    });
  }
}
